import { useState, useEffect } from "react";
import { useUser } from "@clerk/react";
import { IconTrendUp, IconHeartSmall, IconFilmPlay } from "@/components/ui/cartoon-icons";
import { LazyVideo } from "@/components/ui/lazy-video";
import { VideoModal } from "@/components/ui/video-modal";
import { cn } from "@/lib/utils";
import { trackViewContent } from "@/services/analytics";

const S3 = "https://cartoontale.s3.us-east-1.amazonaws.com/examples";

const CATEGORIES = ["All", "Surreal", "Fight", "Scenery"];

const GALLERY = [
  { title: "Melting Time", style: "Surreal", views: "15.1K", likes: "2.8K", video: `${S3}/Surreal/1.mp4` },
  { title: "Dream Shift", style: "Surreal", views: "9.8K", likes: "1.6K", video: `${S3}/Surreal/2.mp4` },
  { title: "Mirror World", style: "Surreal", views: "13.4K", likes: "2.4K", video: `${S3}/Surreal/3.mp4` },
  { title: "Gravity Bend", style: "Surreal", views: "11.2K", likes: "1.9K", video: `${S3}/Surreal/4.mp4` },
  { title: "Void Walker", style: "Surreal", views: "18.3K", likes: "3.5K", video: `${S3}/Surreal/5.mp4` },
  { title: "Fractal Gate", style: "Surreal", views: "22.1K", likes: "4.2K", video: `${S3}/Surreal/6.mp4` },
  { title: "Endless Fall", style: "Surreal", views: "16.7K", likes: "3.0K", video: `${S3}/Surreal/7.mp4` },
  { title: "Time Loop", style: "Surreal", views: "12.5K", likes: "2.1K", video: `${S3}/Surreal/8.mp4` },
  { title: "Shattered Sky", style: "Surreal", views: "20.9K", likes: "3.8K", video: `${S3}/Surreal/9.mp4` },
  { title: "Reality Tear", style: "Surreal", views: "14.6K", likes: "2.6K", video: `${S3}/Surreal/10.mp4` },
  { title: "Neon Drift", style: "Surreal", views: "17.3K", likes: "3.1K", video: `${S3}/Surreal/11.mp4` },
  { title: "First Strike", style: "Fight", views: "31.8K", likes: "5.1K", video: `${S3}/Fight/1.mp4` },
  { title: "Blade Clash", style: "Fight", views: "19.7K", likes: "3.5K", video: `${S3}/Fight/2.mp4` },
  { title: "Thunder Fist", style: "Fight", views: "26.4K", likes: "4.8K", video: `${S3}/Fight/3.mp4` },
  { title: "Shadow Duel", style: "Fight", views: "23.6K", likes: "4.1K", video: `${S3}/Fight/4.mp4` },
  { title: "Power Surge", style: "Fight", views: "28.2K", likes: "4.5K", video: `${S3}/Fight/5.mp4` },
  { title: "Final Blow", style: "Fight", views: "17.4K", likes: "2.9K", video: `${S3}/Fight/6.mp4` },
  { title: "Chain Combo", style: "Fight", views: "21.3K", likes: "3.7K", video: `${S3}/Fight/7.mp4` },
  { title: "Golden Fields", style: "Scenery", views: "8.9K", likes: "1.4K", video: `${S3}/Scenery/1.mp4` },
  { title: "Mountain Dawn", style: "Scenery", views: "14.2K", likes: "2.1K", video: `${S3}/Scenery/2.mp4` },
  { title: "Quiet River", style: "Scenery", views: "11.5K", likes: "1.8K", video: `${S3}/Scenery/3.mp4` },
  { title: "Sunset Ridge", style: "Scenery", views: "16.7K", likes: "3.0K", video: `${S3}/Scenery/4.mp4` },
  { title: "Cherry Lane", style: "Scenery", views: "19.4K", likes: "3.4K", video: `${S3}/Scenery/5.mp4` },
  { title: "Moonlit Lake", style: "Scenery", views: "13.8K", likes: "2.5K", video: `${S3}/Scenery/6.mp4` },
  { title: "Cloud Temple", style: "Scenery", views: "22.6K", likes: "4.0K", video: `${S3}/Scenery/7.mp4` },
  { title: "Forest Path", style: "Scenery", views: "10.3K", likes: "1.7K", video: `${S3}/Scenery/8.mp4` },
  { title: "Starlit Coast", style: "Scenery", views: "15.9K", likes: "2.7K", video: `${S3}/Scenery/9.mp4` },
  { title: "Amber Meadow", style: "Scenery", views: "12.1K", likes: "2.0K", video: `${S3}/Scenery/10.mp4` },
];

export function ExplorePage() {
  const { user } = useUser();
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeVideo, setActiveVideo] = useState<string | null>(null);

  useEffect(() => {
    trackViewContent("Explore", user?.id, { funnel_stage: "explore" });
  }, []);

  const filtered = activeCategory === "All"
    ? GALLERY
    : GALLERY.filter((g) => g.style === activeCategory);

  return (
    <div className="p-5 md:p-8 max-w-6xl mx-auto">
      <div className="mb-8 animate-fade-in-up">
        <h1 className="text-3xl md:text-4xl font-black text-[#241d3a] tracking-tight mb-2">EXPLORE</h1>
        <p className="text-base text-[#241d3a]/55 font-medium">Discover videos, images, and characters from the community</p>
      </div>

      <div className="flex items-center gap-2 mb-8 overflow-x-auto no-scrollbar pb-1 animate-fade-in-up delay-100">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={cn(
              "px-5 py-2 rounded-[12px] text-[13px] font-extrabold whitespace-nowrap border-2 border-[#2a2342] transition-all",
              activeCategory === cat
                ? "bg-[#b8a5f0] text-[#241d3a] shadow-[2px_2px_0_rgba(42,35,66,0.16)]"
                : "bg-[#241d3a]/[0.06] text-[#241d3a] border-[#241d3a]/15 hover:bg-[#241d3a]/[0.08] hover:border-[#241d3a]/15",
            )}
          >
            {cat}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 animate-fade-in delay-200">
        {filtered.map((item, i) => (
          <div
            key={i}
            onClick={() => setActiveVideo(item.video)}
            className="group card-bold overflow-hidden cursor-pointer"
          >
            <div className="relative w-full aspect-square overflow-hidden">
              <LazyVideo src={item.video} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/5 group-hover:bg-transparent transition-colors" />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <IconFilmPlay className="size-12 drop-shadow-lg" />
              </div>
              <span className="absolute top-3 left-3 text-[10px] font-extrabold uppercase tracking-wider px-3 py-1 rounded-full bg-white/80 backdrop-blur-sm text-[#241d3a]">
                {item.style}
              </span>
            </div>
            <div className="px-3 py-3">
              <p className="text-[13px] text-[#241d3a] font-extrabold mb-1">{item.title}</p>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1 text-[11px] text-[#241d3a]/45 font-bold">
                  <IconTrendUp className="size-3.5" />
                  {item.views}
                </span>
                <span className="flex items-center gap-1 text-[11px] text-[#241d3a]/45 font-bold">
                  <IconHeartSmall className="size-3.5" />
                  {item.likes}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {activeVideo && <VideoModal src={activeVideo} onClose={() => setActiveVideo(null)} />}
    </div>
  );
}
